import { FormArray, FormGroup } from '@angular/forms';
import { SpecificationType } from '../SpecLine';
import {
  UpdateSpecificationLine,
  currentSpecExceedsMichelinSpec,
  mapActionDetails,
  resolveDefinitionField
} from './UpdateSpecificationLine';
import { Store } from '@ngxs/store';
import { UpdateLimit } from './UpdateLimit';
import { ProductsState } from 'src/app/shared/state/ProductsState';
import { DealerState } from 'src/app/shared/state/DealerState';
import { TranslationService } from '../../../../shared/translations/services/translation.service';

export interface CreateSpecificationPayload {
  name: string;
  description: string;
  type: string;
  specificationLines: UpdateSpecificationLine[];
  user: string;
}

export function mapToCreateSpecificationPayload(
  camSpecificationForm: any,
  store: Store,
  translationService: TranslationService
): CreateSpecificationPayload {
  let createSpecificationPayload: CreateSpecificationPayload;
  const camSpecificationFormValue = camSpecificationForm.value;

  const specLines: UpdateSpecificationLine[] = [];

  const formArray = camSpecificationForm.get('lines') as FormArray;
  formArray.controls.forEach((control: FormGroup) =>
    specLines.push(generateUpdateSpecificationLine(control.value, store, translationService))
  );

  createSpecificationPayload = {
    name: camSpecificationFormValue.specCode,
    description: camSpecificationFormValue.specDescription,
    type: camSpecificationFormValue.specType,
    specificationLines: specLines,
    user: store.selectSnapshot(DealerState.currentUser)
  };

  return createSpecificationPayload;
}

export function generateUpdateSpecificationLine(specLineForm: any, store: Store, translationService: TranslationService) {
  const limits: UpdateLimit[] = [];
  limits.push(fillLimit(specLineForm.limit0, 1));

  if (specLineForm.secondRepairLimitAdded) {
    limits.push(fillLimit(specLineForm.limit1, 2));
  }

  const specLine: UpdateSpecificationLine = {
    id: '',
    type: specLineForm.type,
    label: specLineForm.label,
    specCode: '',
    specDescription: '',
    defaultProductSpec: specLineForm.isDefault,
    exceedMichelinSpec: currentSpecExceedsMichelinSpec(specLineForm, limits.length, store),
    applicationDefinition: translationService.translateToEnglish(specLineForm.definition.applicationDefinition),
    sizes: resolveDefinitionField(
      specLineForm.definition.sizes.map((x) => translationService.translateToEnglish(x)),
      store.selectSnapshot(ProductsState.products).sizes.length
    ),
    makes: resolveDefinitionField(
      specLineForm.definition.makes.map((x) => translationService.translateToEnglish(x)),
      store.selectSnapshot(ProductsState.products).makes.length
    ),
    axles: resolveDefinitionField(
      specLineForm.definition.axles.map((x) => translationService.translateToEnglish(x)),
      store.selectSnapshot(ProductsState.products).axles.length
    ),
    series: resolveDefinitionField(
      specLineForm.definition.series.map((x) => translationService.translateToEnglish(x)),
      store.selectSnapshot(ProductsState.products).series.length
    ),
    treads:
      specLineForm.type === SpecificationType.REPAIR_RETREAD
        ? resolveDefinitionField(
            specLineForm.definition.treads.map((x) => translationService.translateToEnglish(x)),
            store.selectSnapshot(ProductsState.products).treads.length
          )
        : null,
    ntr: mapActionDetails(specLineForm.ntr, translationService),
    age: mapActionDetails(specLineForm.age, translationService),
    repair: mapActionDetails(specLineForm.limit0, translationService),
    limits
  };

  return specLine;
}

export function fillLimit(formLimit: any, sectionNumber: number): UpdateLimit {
  return {
    sectionNumber,
    totalRepairLimits: {
      totalRepairs: getNullSafeValue(formLimit.totalRepairs),
      nailRepairs: getNullSafeValue(formLimit.totalNail),
      rnailCrownRepairs: getNullSafeValue(formLimit.totalRnailCrown),
      rnailShoulder: {
        repairs: getNullSafeValue(formLimit.totalRnailShoulder),
        shoulder: {
          count: getNullSafeValue(formLimit.totalRnailShoulder),
          size: formLimit.rNailShoulderSize
        }
      },
      section: {
        repairs: getNullSafeValue(formLimit.totalSection),
        crown: {
          count: getNullSafeValue(formLimit.totalCrownSection),
          size: formLimit.maxCrownSize
        },
        shoulder: {
          count: getNullSafeValue(formLimit.totalShoulderSection),
          size: formLimit.maxShoulderSize
        },
        sidewall: {
          count: getNullSafeValue(formLimit.totalSidewallSection),
          size: formLimit.maxSidewallSize
        }
      }
    },
    otherRepairLimits: {
      spot: getNullSafeValue(formLimit.spot),
      bead: getNullSafeValue(formLimit.bead),
      liner: getNullSafeValue(formLimit.liner),
      brake: getNullSafeValue(formLimit.brakeSkid)
    }
  };
}

function getNullSafeValue(v: number) {
  return v;
}
